import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/lib/trpc/router';
import type {
  FacesheetCriteriaRow,
  FacesheetCriteriaSectionData,
  FacesheetDemographicsData,
  FacesheetHtmlSummaryData,
  FacesheetReference,
  FacesheetViewerDocument,
} from './types';

type GetPatientFacesheetOutputResponse =
  inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetOutput'];

type OutputDemographics = NonNullable<
  GetPatientFacesheetOutputResponse['demographics']
>;

type OutputRow =
  GetPatientFacesheetOutputResponse['sections'][number]['rows'][number];

export type DemographicsFidelity = 'HIGH' | 'MEDIUM' | 'LOW';

const DEFAULT_DEMOGRAPHICS: FacesheetDemographicsData = {
  fullName: 'Unknown Patient',
  dateOfBirth: 'Unknown',
  gender: 'Unknown',
  phone: 'Unknown',
  address: 'Unknown',
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const compactJoin = (values: Array<string | null | undefined>) =>
  values.filter(Boolean).join(' ');

const formatAddress = (
  address: OutputDemographics['address'] | null | undefined,
) => {
  if (!address) return 'Unknown';

  return (
    [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.zipCode,
      address.country,
    ]
      .filter(Boolean)
      .join(', ') || 'Unknown'
  );
};

export const getDemographicsSectionTitle = (
  fidelity: DemographicsFidelity | null | undefined,
) => {
  if (fidelity === 'HIGH') return 'Demographics - High Fidelity';
  if (fidelity === 'MEDIUM') return 'Demographics - Medium Fidelity';
  if (fidelity === 'LOW') return 'Demographics - Low Fidelity';
  return 'Patient Demographics';
};

const normalizeDemographics = (
  demographics: OutputDemographics | null | undefined,
): FacesheetDemographicsData => {
  if (!demographics) return DEFAULT_DEMOGRAPHICS;

  return {
    fullName:
      compactJoin([
        demographics.firstName,
        demographics.middleName,
        demographics.lastName,
      ]) || DEFAULT_DEMOGRAPHICS.fullName,
    dateOfBirth: demographics.dateOfBirth ?? DEFAULT_DEMOGRAPHICS.dateOfBirth,
    gender: demographics.gender ?? DEFAULT_DEMOGRAPHICS.gender,
    phone: demographics.phone ?? DEFAULT_DEMOGRAPHICS.phone,
    address: formatAddress(demographics.address),
    fidelity: demographics.fidelity,
    source: demographics.source,
  };
};

const normalizeReferences = (
  references: OutputRow['references'],
  rowId: string,
): FacesheetReference[] =>
  references.map((reference, index) => ({
    id: reference.id || `${rowId}-reference-${index + 1}`,
    label: reference.label || `Reference ${index + 1}`,
    dateReferenced: reference.dateReferenced ?? null,
    scrollTarget: {
      ...(reference.reportScope && reference.curatedRecordId
        ? {
            kind: 'anchor' as const,
            anchorId: `hie-record-${reference.curatedRecordId}`,
          }
        : { kind: 'page' as const, pageNumber: index + 1 }),
    },
    curatedRecordId: reference.curatedRecordId,
    reportScope: reference.reportScope,
  }));

const buildRowTree = (
  rows: OutputRow[],
  sectionId: string,
  parentRowId: string | null = null,
): FacesheetCriteriaRow[] =>
  rows
    .filter((row) => row.parentRowId === parentRowId)
    .map((row) => {
      const rowId = `${sectionId}-${slugify(row.id)}`;

      return {
        id: rowId,
        anchorId: `row-${rowId}`,
        sourceRowId: row.id,
        parentRowId: row.parentRowId,
        depth: row.depth,
        question: row.question,
        answer: row.answer.trim() || '--',
        reason: row.reason ?? '',
        references: normalizeReferences(row.references, rowId),
        isHighlighted: row.highlightBoolean ?? false,
        children: buildRowTree(rows, sectionId, row.id),
      };
    });

const normalizeSection = (
  section: GetPatientFacesheetOutputResponse['sections'][number],
): FacesheetCriteriaSectionData => {
  const sectionId = slugify(section.title || section.id);

  return {
    id: sectionId,
    title: section.title,
    anchorId: `section-${sectionId}`,
    rows: buildRowTree(section.rows, sectionId),
  };
};

export const normalizeFacesheetOutput = (
  output: GetPatientFacesheetOutputResponse,
): FacesheetViewerDocument => {
  const summary: FacesheetHtmlSummaryData = {
    demographics: normalizeDemographics(output.demographics),
    criteriaSections: output.sections.map(normalizeSection),
  };

  return {
    id: output.facesheetRequestId,
    title: output.title,
    generatedAt: output.completedAt ? new Date(output.completedAt) : new Date(),
    summaryPdfSignedUrl: output.summaryPdfSignedUrl,
    allReferencesZipSignedUrl: output.allReferencesZipSignedUrl,
    summary,
  };
};
